import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchFullAnalysisForUsecase } from '@/features/datasets/utils/processAnalysis';

/**
 * Fetches the data analysis for a single usecase and returns the same shape
 * `DataAnalysisSection` consumes today (`{data, benchmarkData,
 * externalModelCounts, datasetsInfo, optuneSpecificInfo}`).
 *
 * Refetches automatically when the usecase identity, attached files
 * (datasets / benchmarks / manual_model_predictions) or NER tab change. Stale
 * responses from earlier usecases are dropped so tab switching can never push
 * the previous task's analysis into the active tab.
 *
 * Used by `DataOverviewCard` for every objective in a task sub-group; legacy
 * hosts (where applicable) keep their existing page-level fetch and
 * passes the result through `analysisOverrides`.
 *
 * @param {Object} usecase
 * @param {Object} [opts]
 * @param {string} [opts.activeTab] NER vs relations toggle.
 * @param {boolean} [opts.enabled=true] Disable to skip fetching (e.g. tab not yet selected).
 * @param {string|number} [opts.refreshKey] Bump to force a refetch even if
 *   the usecase / file signature is unchanged. Used by the parent card to
 *   pull a fresh `dataset_info` snapshot once the data_processor finishes
 *   processing an in-flight upload (the initial fetch lands while
 *   `num_records` is still 0).
 */
const useUsecaseDataOverview = (
    usecase,
    { activeTab = 'entities', enabled = true, refreshKey = 0 } = {},
) => {
    const [state, setState] = useState({
        data: null,
        benchmarkData: null,
        externalModelCounts: null,
        datasetsInfo: [],
        optuneSpecificInfo: null,
        isLoading: false,
    });

    const requestIdRef = useRef(0);

    const fileSignature = (usecase?.datasets || []).join('|')
        + '#' + (usecase?.benchmarks || []).join('|')
        + '#' + (usecase?.manual_model_predictions || []).join('|');

    const refresh = useCallback(async () => {
        if (!enabled || !usecase?._id) {
            return;
        }
        const requestId = ++requestIdRef.current;
        setState((prev) => ({ ...prev, isLoading: true }));
        try {
            const result = await fetchFullAnalysisForUsecase({
                usecase,
                activeTab,
                // Surface `datasetsInfo` as soon as the metadata call resolves
                // (still loading the heavy analysis): the file tiles read the
                // AI-generated / AI-retrieved pill and file size from it, so
                // only the size keeps a spinner while analysis is in flight.
                onMetadata: ({ datasetsInfo }) => {
                    if (requestIdRef.current !== requestId) return;
                    setState((prev) => ({ ...prev, datasetsInfo }));
                },
            });
            if (requestIdRef.current !== requestId) return;
            setState({ ...result, isLoading: false });
        } catch (error) {
            if (requestIdRef.current !== requestId) return;
            console.error('useUsecaseDataOverview: refresh failed', error);
            setState({
                data: null,
                benchmarkData: null,
                externalModelCounts: null,
                datasetsInfo: [],
                optuneSpecificInfo: null,
                isLoading: false,
            });
        }
    }, [enabled, usecase, activeTab]);

    useEffect(() => {
        if (!enabled) return;
        if (!usecase?._id) {
            setState({
                data: null,
                benchmarkData: null,
                externalModelCounts: null,
                datasetsInfo: [],
                optuneSpecificInfo: null,
                isLoading: false,
            });
            return;
        }
        refresh();
    }, [enabled, usecase?._id, fileSignature, activeTab, refreshKey, refresh]);

    return { ...state, refresh };
};

export default useUsecaseDataOverview;
